// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Ingredienti, materie prime e allergeni"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Schermata ........ App.js, GestioneIngredienti
//   Categorie ........ App.js, costante CAT_INGREDIENTE
//   Unità di misura .. App.js, costante UNITA_MISURA (8 valori)
//   Allergeni ........ App.js, costante ALLERGENI (14 voci, allineate all'All. II
//                      del Reg. UE 1169/2011)
//   Ricerca nutriz. .. backend/routes/nutrition.js — due fonti: tabella interna
//                      tr_nutrizionali_base (etichettata "DB Base (CREA)") e
//                      Open Food Facts. Se la query è 8-14 cifre viene trattata
//                      come codice a barre e si interroga solo OFF.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'i0', label: 'Prima di iniziare' },
  { id: 'i1', label: '1. Le cinque categorie' },
  { id: 'i2', label: "2. L'unità di misura" },
  { id: 'i3', label: '3. Gli allergeni' },
  { id: 'i4', label: '4. I valori nutrizionali' },
  { id: 'i5', label: '5. Il fornitore principale' },
  { id: 'i6', label: 'Le trappole' },
  { id: 'i7', label: 'Domande frequenti' },
];

const CATEGORIE = [
  ['MATERIA PRIMA', 'Quello che compri e che trasformi: il latte, la carne, la farina.'],
  ['INGREDIENTE',   'Quello che entra nella ricetta in quantità minori: sale, spezie, aromi.'],
  ['ADDITIVO',      'Conservanti, correttori, coadiuvanti. Vanno distinti perché in etichetta hanno regole loro.'],
  ['MOCA',          'Materiali e oggetti a contatto: budelli, buste, vasetti, pellicole. Non sono alimenti ma vanno tracciati.'],
  ['PACKAGING',     "L'imballo esterno: cartoni, pallet, etichette. Non tocca il prodotto."],
];

const ALLERGENI = [
  'Glutine (cereali)', 'Crostacei', 'Uova', 'Pesce', 'Arachidi', 'Soia', 'Latte',
  'Frutta a guscio', 'Sedano', 'Senape', 'Sesamo', 'Solfiti / SO₂', 'Lupini', 'Molluschi',
];

const UM = ['kg', 'g', 'l', 'ml', 'pz', 'conf', 'sacchi', 'cartoni'];

export default function IngredientiAllergeni() {
  return (
    <GuidaLayout
      titolo="Ingredienti, materie prime e allergeni"
      sottotitolo="L'elenco di tutto quello che entra nei vostri prodotti. Due campi decidono più di quanto sembri: l'unità di misura e gli allergeni."
      durata="~ 12 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* i0 */}
      <H id="i0">Prima di iniziare</H>

      <P>
        Qui si elenca <Strong>tutto quello che entra</Strong>: le materie prime, gli ingredienti
        minori, gli additivi, e anche i materiali che toccano il prodotto senza farne parte, come
        i budelli e le buste. Ogni voce si inserisce una volta e poi la si ritrova ovunque: nelle
        bolle di ingresso, nelle ricette, nelle schede tecniche.
      </P>
      <P>
        Come per le altre anagrafiche, l'unico campo obbligatorio è il <Strong>Nome</Strong>. Ma
        due degli altri conviene compilarli con attenzione già la prima volta, perché correggerli
        dopo costa: l'<Strong>unità di misura</Strong>, che si porta dietro tutti i conti, e gli{' '}
        <Strong>allergeni</Strong>, da cui nasce quello che scriverete in etichetta.
      </P>

      <Callout kind="tip" titolo="Non serve elencare tutto il mondo">
        Mettete quello che usate davvero. Un ingrediente che avete comprato una volta tre anni fa
        non serve a nessuno, e allunga le tendine che l'operatore deve scorrere ogni giorno.
      </Callout>

      {/* i1 */}
      <H id="i1" cat="temperature">1. Le cinque categorie</H>

      <P>
        La <Strong>Categoria</Strong> è una scelta fra cinque. Non cambia il funzionamento del
        programma, ma serve a tenere ordinati elenchi che diventano lunghi, e a distinguere
        alimenti da non-alimenti.
      </P>

      <div style={{ margin: '18px 0' }}>
        {CATEGORIE.map(([nome, cosa]) => (
          <div key={nome} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '9px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <span style={{
              flexShrink: 0, width: 128, textAlign: 'center',
              padding: '3px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 800,
              letterSpacing: 0.3, background: LT.bluSoft, color: LT.blu,
            }}>{nome}</span>
            <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.5 }}>{cosa}</div>
          </div>
        ))}
      </div>

      <P>
        La distinzione fra <Code>MATERIA PRIMA</Code> e <Code>INGREDIENTE</Code> è una comodità
        vostra, non una regola: mettete in materia prima quello che costituisce il grosso del
        prodotto. Quella fra <Code>MOCA</Code> e <Code>PACKAGING</Code> invece ha un senso
        preciso: i MOCA toccano l'alimento e hanno adempimenti propri — le dichiarazioni di
        conformità che vi chiede l'ispettore — mentre il cartone che sta fuori non li ha.
      </P>

      {/* i2 */}
      <H id="i2">2. L'unità di misura</H>

      <P>
        Otto possibilità:
      </P>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, margin: '12px 0 18px' }}>
        {UM.map(u => (
          <span key={u} style={{
            padding: '5px 13px', borderRadius: LT.r.md, fontFamily: LT.mono,
            fontSize: 13, fontWeight: 700, background: LT.n100, color: LT.n700,
            border: `1px solid ${LT.n200}`,
          }}>{u}</span>
        ))}
      </div>

      <P>
        Sembra il campo più banale della scheda ed è quello che porta più conseguenze, perché{' '}
        <Strong>accompagna l'ingrediente per tutta la filiera</Strong>: la quantità nella bolla di
        ingresso, la giacenza in magazzino, la dose nella ricetta, il consumo nel bilancio di
        massa. Sono tutti nella stessa unità.
      </P>
      <P>
        La regola pratica è: <Strong>scegliete l'unità in cui vi arriva la merce</Strong>. Se il
        latte arriva in litri, litri. Se il sale arriva in sacchi da 25 kg e voi lo dosate a chili,
        mettete kg — così la dose in ricetta è quella che pesate davvero. E{' '}
        <Code>conf</Code> serve per quello che si conta a confezioni, come i budelli.
      </P>

      <Callout kind="warn" titolo="Cambiarla dopo non converte i numeri">
        Se modificate l'unità di misura di un ingrediente già usato, il programma non ricalcola
        niente: le quantità già registrate restano quei numeri, e da lì in avanti significheranno
        un'altra cosa. Prima di cambiarla su un ingrediente che gira da mesi, fermatevi a pensare
        se non sia meglio crearne uno nuovo.
      </Callout>

      {/* i3 */}
      <H id="i3" cat="pulizie">3. Gli allergeni</H>

      <P>
        Quattordici caselle, che sono esattamente le quattordici sostanze dell'Allegato II del
        Regolamento UE 1169/2011 — quelle che per legge vanno dichiarate in etichetta.
      </P>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, margin: '14px 0 18px' }}>
        {ALLERGENI.map(a => (
          <span key={a} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
            background: LT.rossoSoft, color: '#B91C1C', border: '1px solid #FBD5D5',
          }}>
            <Ico d={I.alert} size={12} stroke="#B91C1C" />{a}
          </span>
        ))}
      </div>

      <P>
        Si spuntano <Strong>sull'ingrediente</Strong>, una volta sola. Il vantaggio arriva dopo:
        quando comporrete la ricetta di un prodotto, gli allergeni dei suoi ingredienti si
        ritrovano lì, e da lì finiscono nella scheda tecnica. È il motivo per cui vale la pena
        spuntarle con precisione adesso invece di ricostruirle prodotto per prodotto ogni volta
        che serve un'etichetta.
      </P>

      <Callout kind="warn" titolo="Spunta quello che contiene, non quello che sembra">
        Due errori frequenti e opposti. Il primo è dimenticare gli allergeni nascosti: un aroma
        può contenere soia, un addensante può contenere glutine, un formaggio contiene latte anche
        se si chiama "caglio". Il secondo è spuntare tutto per prudenza, che rende l'informazione
        inutile e vi obbliga a scrivere in etichetta cose non vere. La fonte giusta è la{' '}
        <Strong>scheda tecnica del fornitore</Strong>: se non ce l'avete, chiedetela — è un
        documento che deve darvi.
      </Callout>

      {/* i4 */}
      <H id="i4" cat="formazione">4. I valori nutrizionali</H>

      <P>
        In fondo alla scheda c'è un blocco <Strong>"Valori nutrizionali"</Strong>, con il
        sottotitolo "Etichettatura per 100 g di prodotto · Reg. UE 1169/2011": energia, grassi,
        grassi saturi, carboidrati, zuccheri, proteine, sale, fibre. Servono per la scheda tecnica
        e per l'etichetta.
      </P>
      <P>
        Compilarli a mano per ogni ingrediente sarebbe un lavoro da giorni. Per questo c'è una
        cosa che quasi nessuno scopre da solo: il pulsante{' '}
        <Tasto>Cerca in DB/Online</Tasto>.
      </P>

      <Passo n={1} titolo="Cerca per nome">
        <P style={{ margin: '6px 0' }}>
          Scrivi il nome dell'ingrediente e premi <Tasto>Cerca</Tasto>. Il programma guarda in due
          posti: una <Strong>banca dati alimentare italiana</Strong> già dentro LabThree — i
          risultati sono marcati <Code>DB Base (CREA)</Code> — e il catalogo online{' '}
          <Strong>Open Food Facts</Strong>. Per una materia prima generica, tipo "latte intero" o
          "farina di frumento", la prima fonte è la più affidabile.
        </P>
      </Passo>
      <Passo n={2} titolo="Oppure cerca il codice a barre">
        <P style={{ margin: '6px 0' }}>
          Nello stesso campo puoi incollare il <Strong>codice EAN</Strong> stampato sulla
          confezione, quelle otto-tredici cifre sotto le barre. Se scrivi solo numeri il programma
          capisce che è un barcode e cerca il prodotto preciso. È la strada più rapida per un
          ingrediente confezionato che avete in mano.
        </P>
      </Passo>
      <Passo n={3} titolo="Applica e correggi">
        <P style={{ margin: '6px 0' }}>
          Dai risultati scegli quello giusto e i campi si riempiono da soli. Restano{' '}
          <Strong>modificabili</Strong>: se il fornitore vi ha dato una scheda con numeri suoi,
          quelli valgono più di qualsiasi banca dati.
        </P>
      </Passo>

      <Callout kind="info" titolo="Un valore preso da internet non è la tua analisi">
        Open Food Facts è un archivio collaborativo: utilissimo per partire, non una certificazione.
        Per i valori che finiscono su un'etichetta di cui rispondete voi, la fonte corretta resta
        la scheda del fornitore o un'analisi di laboratorio. La ricerca automatica serve a non
        partire dal foglio bianco, non a sostituire il documento.
      </Callout>

      {/* i5 */}
      <H id="i5">5. Il fornitore principale</H>

      <P>
        Il campo <Strong>Fornitore principale</Strong> collega l'ingrediente a chi ve lo vende di
        solito. Non è vincolante: potete registrare un ingresso dello stesso ingrediente da un
        altro fornitore senza che nulla si lamenti. Serve a farvi risparmiare la scelta nel caso
        normale, e a rispondere in fretta alla domanda "chi ci vende questo".
      </P>
      <P>
        Se un ingrediente lo comprate da tre fornitori diversi a rotazione, mettete quello più
        frequente e non pensateci più: la tracciabilità vera non passa da qui, passa dal lotto
        indicato nella bolla di ingresso.
      </P>

      {/* i6 */}
      <H id="i6">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ["L'unità di misura si trascina dietro tutto",
            'Bolla, giacenza, dose in ricetta e bilancio di massa parlano tutti in quella unità. Cambiarla dopo non converte i numeri già scritti.'],
          ['Gli allergeni non si deducono dal nome',
            'Il programma non li indovina: se non li spunti, per lui quell\'ingrediente non ne ha. E la scheda tecnica del prodotto lo dirà.'],
          ['Nomi doppi sono possibili',
            'Nessun controllo sui duplicati. "Sale marino" e "sale marino fino" diventano due voci con due storie separate: usa la ricerca in cima alla lista prima di crearne uno nuovo.'],
          ['I MOCA sono qui, non fra i prodotti',
            'Budelli, buste e vasetti si inseriscono come ingredienti con categoria MOCA. È il posto giusto: entrano con una bolla e hanno un lotto da tracciare come tutto il resto.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* i7 */}
      <H id="i7">Domande frequenti</H>

      <FAQ q="Devo inserire l'acqua fra gli ingredienti?">
        Se è un ingrediente della ricetta — e in molte lavorazioni lo è — conviene, perché il
        bilancio di massa torna solo se l'acqua è contata. Come unità mettete litri.
      </FAQ>

      <FAQ q="Un ingrediente che uso in due prodotti diversi va inserito due volte?">
        No, una volta sola. Le ricette lo richiamano dallo stesso elenco, ognuna con la sua
        quantità. È il vantaggio di tenere l'anagrafica pulita: cambiando gli allergeni in un
        punto si aggiornano tutti i prodotti che lo usano.
      </FAQ>

      <FAQ q="I valori nutrizionali sono obbligatori?">
        Per l'anagrafica no, si può salvare senza. Servono quando volete produrre la scheda
        tecnica o l'etichetta di un prodotto: senza i valori degli ingredienti, quei documenti
        restano incompleti.
      </FAQ>

      <FAQ q="Come faccio se un ingrediente non lo trovo in nessuna banca dati?">
        Compili i valori a mano dalla scheda del fornitore. La ricerca automatica è una
        scorciatoia, non l'unica strada, e i campi sono sempre scrivibili.
      </FAQ>

      <FAQ q="Posso cancellare un ingrediente che ho inserito per errore?">
        Se non è ancora stato usato in nessuna ricetta né in nessuna bolla, sì. Se invece è già
        agganciato a qualcosa non si toglie, per non aprire buchi nella catena: la strada è
        rinominarlo o smettere di usarlo.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Inserisci gli ingredienti di <em>un</em> prodotto, con unità di misura e allergeni, e
          passa a <Strong>prodotti e semilavorati</Strong>. Con quelli due elenchi a posto potrai
          scrivere la prima ricetta, che è il passo in cui tutto si tiene insieme.
        </div>
      </div>

    </GuidaLayout>
  );
}
